import React from 'react';
import { Chip, MonatsPips, PersonAvatar, saldoAnzeige } from '../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { PORTAL_ANZEIGE, STATUS_ANZEIGE, statusLabel, type MieterZeile } from '../../../../lib/mieter/liste';

export { saldoAnzeige };

export function seitText(z: Pick<MieterZeile, 'status' | 'beginn' | 'ende'>): string {
  if (z.status === 'einzug' && z.beginn) return `Einzug ${deDatum(z.beginn)}`;
  if (z.status === 'inaktiv') return z.ende ? `Ausgezogen ${deDatum(z.ende)}` : '';
  return z.beginn ? `seit ${deDatum(z.beginn)}` : '';
}

/** Avatar mit Initialen; Punkt = Portalstatus (README §5.1). */
export function MieterAvatar({ z, groesse = 48 }: { z: Pick<MieterZeile, 'name' | 'inaktiv' | 'portal'>; groesse?: 32 | 48 }) {
  const portal = PORTAL_ANZEIGE[z.portal];
  return <PersonAvatar text={initialen(z.name)} ton={z.inaktiv ? 'grau' : 'acc'} groesse={groesse} punkt={{ farbe: portal.farbe, label: portal.label }} />;
}

/** Memoisiert: bei 500+ Mietern rendern beim Tippen in der Suche nur geänderte Kacheln neu. */
export const MieterKachel = React.memo(function MieterKachel({ z, neu, onOeffnen }: { z: MieterZeile; neu: boolean; onOeffnen: (id: string) => void }) {
  const st = STATUS_ANZEIGE[z.status];
  const saldo = saldoAnzeige(z.saldo);
  const tastatur = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(z.id); }
  };
  const ort = [z.objektName, z.einheit].filter(Boolean).join(' · ');
  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`${z.name} öffnen`}
      onClick={() => onOeffnen(z.id)}
      onKeyDown={tastatur}
      className="immo-glas immo-hover-kachel flex flex-col cursor-pointer min-w-0"
      style={{ borderRadius: 26, padding: 16, gap: 14, ...(neu ? { boxShadow: 'inset 0 0 0 2px var(--ok), var(--glass-sh)' } : {}) }}
    >
      <div className="flex items-center min-w-0" style={{ gap: 12 }}>
        <MieterAvatar z={z} />
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
          <span className="truncate" style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>{z.name}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{ort || 'Keine Einheit zugeordnet'}</span>
        </div>
        <Chip ton={st.ton} punkt>{statusLabel(z.status, z.ehemalig)}</Chip>
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="flex flex-col min-w-0" style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: 'var(--fill)' }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Gesamtmiete</span>
          <b className="immo-zahl" style={{ fontSize: 17, fontWeight: 800 }}>{eur(z.gesamt)}</b>
        </div>
        <div className="flex flex-col min-w-0" title={saldo.titel}
          style={{ borderRadius: 16, padding: '10px 12px', gap: 1, background: saldo.rueckstand ? 'var(--bad-soft)' : 'var(--fill)' }}>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Mietkonto</span>
          <b className="immo-zahl truncate" style={{ fontSize: 17, fontWeight: 800, color: saldo.farbe }}>{saldo.text}</b>
        </div>
      </div>
      <div className="flex items-center" style={{ gap: 8 }}>
        <MonatsPips pips={z.pips} />
        <span className="whitespace-nowrap" style={{ fontSize: 11, color: 'var(--ink3)' }}>{seitText(z)}</span>
      </div>
    </article>
  );
});
